import { Serializer, State, BLOCKS } from '@wikifactory/markup-it';
import markupMarkdown from '@wikifactory/markup-it/lib/markdown';
import utils from '@wikifactory/markup-it/lib/markdown/utils';
import trimTrailingLines from 'trim-trailing-lines';
// eslint-disable-next-line
import indentString from 'indent-string';

import {
  supportedVideoSites,
  videoInfoToURL,
} from 'libs/editor/views/mediaInput';

// FIXME - review this file

// FIXME - Find a better name for this function. It returns the number of empty paragraphs
// until the next non-empty node and the state at that point.
const emptyParagraphsStartingAt = fromState => {
  let shiftedState = fromState.shift();
  let currentNode = shiftedState.peek();
  let counter = 0;
  while (
    currentNode &&
    currentNode.type === 'paragraph' &&
    currentNode.text === ''
  ) {
    // eslint-disable-next-line no-plusplus
    counter = ++counter;
    shiftedState = shiftedState.shift();
    currentNode = shiftedState.peek();
  }
  return { counter, shiftedState };
};

const videoTags = supportedVideoSites.join('|');

const inline = {
  escape: /^\\([\\`*{}\[\]()#$+\-.!_>|])/,
  video: new RegExp(`^@\\[(${videoTags})\\]\\((([\\w-]+)|href)\\)`),
  text: /^[\s\S]+?(?=[\\<!@+\[_*`$]| {2,}\n|$)/,
  variable: /^{{\s*(.*?)\s*(?=[#%}]})}}/,
};
inline.text = utils.replace(inline.text)(']|', '~]|')('|', '|https?://|')(
  '~]|',
  `~]|${inline.variable.source}|`,
)();
inline.escape = utils.replace(inline.escape)('])', '~|])')();

const escapedButQuotesAndParen = /(?:\\\(|\\\)|\\\"|\\\'|[^()"'])/;
const escapedButQuotes = /(?:\\\(|\\\)|\\\"|\\\'|[^"'])/;

inline._href = /\s*<?(escapedHref*)>?(?:\s+['"](escapedTitle*?)['"])?\s*/;
inline._href = utils.replace(inline._href)(
  'escapedHref',
  escapedButQuotesAndParen,
)('escapedTitle', escapedButQuotes)();
inline.video = utils.replace(inline.video)('href', inline._href)();

const linkifiedLinkSerialize = Serializer()
  .matchType('linkified-link')
  .then(state => {
    const node = state.peek();

    return state.shift().write(node.text);
  });

const previewSerializeCommon = state => {
  const node = state.peek();
  const { data } = node;

  const href = data.get('href');
  const alt = data.get('alt');
  const output = alt && href ? `[${alt}](${href})` : '';
  return output;
};

const previewBlockSerialize = Serializer()
  .matchType('preview-block')
  .then(state => {
    let output = previewSerializeCommon(state);
    const { counter, shiftedState } = emptyParagraphsStartingAt(state);
    output += `\n\n${'\u200B\n\n'.repeat(counter)}`;
    return shiftedState.write(output);
  });

const previewSerialize = Serializer()
  .matchType('preview')
  .then(state => {
    const output = previewSerializeCommon(state);
    return state.shift().write(output);
  });

const videoSerializeCommon = state => {
  const node = state.peek();
  const { data } = node;

  const src = data.get('src');
  const site = utils.escape(data.get('site') || '');
  let output = '';
  if (src && site) {
    const url = utils.escapeURL(videoInfoToURL(src, site));
    output = `@[${site}](${url})`;
  }
  return output;
};

const videoBlockSerialize = Serializer()
  .matchType('video-block')
  .then(state => {
    let output = videoSerializeCommon(state);
    const { counter, shiftedState } = emptyParagraphsStartingAt(state);
    output += `\n\n${'\u200B\n\n'.repeat(counter)}`;
    return shiftedState.write(output);
  });

const videoSerialize = Serializer()
  .matchType('video')
  .then(state => {
    const output = videoSerializeCommon(state);
    return state.shift().write(output);
  });

const paragraphSerialize = Serializer()
  .matchType('paragraph')
  .then(state => {
    const node = state.peek();
    const { counter, shiftedState } = emptyParagraphsStartingAt(state);
    const inner = state
      .use('inline')
      .setProp('hardlineBreak', true)
      .serialize(node.nodes);

    const out = `${inner}\n\n${'\u200B\n\n'.repeat(counter)}`;
    return shiftedState.write(out);
  });

const imageBlockSerialize = Serializer()
  .matchType('image-block')
  .then(state => {
    const node = state.peek();
    const { data } = node;

    const { counter, shiftedState } = emptyParagraphsStartingAt(state);

    const src = utils.escapeURL(data.get('src') || '');
    const alt = utils.escape(data.get('alt') || '');
    const title = utils.escape(data.get('title') || '');

    let output;

    if (title) {
      output = `![${alt}](${src} "${title}")`;
    } else {
      output = `![${alt}](${src})`;
    }

    output += `\n\n${'\u200B\n\n'.repeat(counter)}`;

    return shiftedState.write(output);
  });

// FIXME - This function is just copy-pasted from markup-it. Send PR so it can be exported from there.
function serializeListItem(state, list, item, index) {
  // Is it a task item ?
  const hasChecked = item.data.has('checked');
  const isChecked = item.data.get('checked');

  // Is it a loose list?
  const loose = item.nodes.some(child => child.type === BLOCKS.PARAGRAPH);

  // Is it the last item from the list?
  const last = list.nodes.size - 1 === index;

  // Calcul bullet to use
  const bullet = list.type === BLOCKS.OL_LIST ? `${index + 1}.` : '*';

  // Indent all lignes
  const indent = bullet.length + 1;
  let body = state.use('block').serialize(item.nodes);
  // Remove unwanted empty lines added by sub-blocks
  body = `${trimTrailingLines(body)}\n`;

  body = indentString(body, ' ', indent).slice(indent);

  if (loose || last) {
    // Add empty line
    body += '\n';
  }

  if (hasChecked) {
    body = `${isChecked ? '[x]' : '[ ]'} ${body}`;
  }

  return `${bullet} ${body}`;
}

const listSerialize = Serializer()
  .matchType([BLOCKS.UL_LIST, BLOCKS.OL_LIST])
  .then(state => {
    const list = state.peek();
    const { nodes } = list;

    let output = nodes
      .map((item, index) => serializeListItem(state, list, item, index))
      .join('');
    const { counter, shiftedState } = emptyParagraphsStartingAt(state);
    output += '\u200B\n\n'.repeat(counter);

    return shiftedState.write(output);
  });

const codeblockSerialize = Serializer()
  .matchType(BLOCKS.CODE)
  .then(state => {
    const node = state.peek();
    const { nodes, data } = node;

    // Escape the syntax
    // http://spec.commonmark.org/0.15/#example-234
    const syntax = utils.escape(data.get('syntax') || '');

    // Get inner content and number of fences
    const innerText = nodes.map(line => line.text).join('\n');
    const hasFences = innerText.indexOf('`') >= 0;

    let output;
    const { counter, shiftedState } = emptyParagraphsStartingAt(state);

    // Use fences if syntax is set
    if (!hasFences || syntax) {
      const hasSyntax = Boolean(syntax);
      output = `${
        `${'```'}${hasSyntax ? syntax : ''}\n` +
        `${innerText}\n` +
        `${'```'}\n\n`
      }${'\u200B\n\n'.repeat(counter)}`;

      return shiftedState.write(output);
    }

    output = `${nodes
      .map(({ text }) => {
        if (!text.trim()) {
          return '';
        }
        return `    ${text}`;
      })
      .join('\n')}\n\n${'\u200B\n\n'.repeat(counter)}`;

    return shiftedState.write(output);
  });

function cellToText(state, cell) {
  const { nodes } = cell;

  if (nodes === undefined) {
    return state.use('inline').serialize(cell.leaves.get(0));
  }

  // The cell may contain a single paragraph,
  // we just want to serialize the inner
  let nodesToSerialize;
  if (nodes.size === 1 && nodes.first().type === BLOCKS.PARAGRAPH) {
    nodesToSerialize = nodes.first().nodes;
  } else {
    nodesToSerialize = nodes;
  }

  return state.use('inline').serialize(nodesToSerialize);
}

function rowToText(state, row) {
  const { nodes } = row;
  return `| ${nodes.map(cell => cellToText(state, cell)).join(' | ')} |`;
}

function alignsToText(aligns) {
  return `|${aligns
    .map(align => {
      if (align === 'right') {
        return ' ---: |';
      }
      if (align === 'center') {
        return ' :---: |';
      }
      if (align === 'left') {
        return ' :--- |';
      }
      return ' --- |';
    })
    .join('')}`;
}

const tableSerialize = Serializer()
  .matchType('table')
  .then(state => {
    const table = state.peek();
    const { data, nodes } = table;

    const align = data.get('align');
    const aligns = data.get('aligns');
    const headerRow = nodes.get(0);
    const bodyRows = nodes.slice(1);

    let output = `${rowToText(state, headerRow)}\n`;

    let alignsInfo = '';
    // If the md doesn't have the "aligns (:---), use the first approach"
    // which iterates through all the columns of the header
    if (aligns === undefined && align !== undefined) {
      alignsInfo = alignsToText(headerRow.nodes);
    } else if (aligns !== undefined && align === undefined) {
      // Otherwise, use the aligns approach, which will add the right align
      alignsInfo = alignsToText(aligns);
    } else {
      alignsInfo = '';

      for (let c = 0; c < headerRow.nodes.size; c += 1) {
        alignsInfo += '| --- ';
      }
      alignsInfo += '|';
    }

    output += `${alignsInfo}\n`;

    output += `${bodyRows.map(row => rowToText(state, row)).join('\n')}\n\n`;

    return state.shift().write(output);
  });

markupMarkdown.block.unshift({ serialize: previewBlockSerialize });
markupMarkdown.block.unshift({
  serialize: imageBlockSerialize,
});
markupMarkdown.block.unshift({
  serialize: videoBlockSerialize,
});
markupMarkdown.block.unshift({
  serialize: paragraphSerialize,
});
markupMarkdown.block.unshift({
  serialize: listSerialize,
});
markupMarkdown.block.unshift({ serialize: codeblockSerialize });

markupMarkdown.block.unshift({ serialize: tableSerialize });

markupMarkdown.inline.unshift({ serialize: previewSerialize });
markupMarkdown.inline.unshift({
  serialize: videoSerialize,
});
markupMarkdown.inline.unshift({ serialize: linkifiedLinkSerialize });

export const parser = State.create(markupMarkdown);
